import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import {
  ClipboardList,
  Calendar,
  FileText,
  MessageSquarePlus,
  MapPin,
  HelpCircle,
  CheckCircle2,
  Send,
  RefreshCw,
  Users,
  AlertTriangle,
  Phone,
  CalendarDays
} from 'lucide-react';

const GUIDE_VERSION = 2;

const Section = ({ icon: Icon, title, children, important }) => (
  <div className="mb-3">
    <div className="d-flex align-items-center gap-2 mb-2">
      <Icon size={18} style={{ color: important ? 'var(--ui-color-warning, #f59e0b)' : 'var(--ui-color-primary)' }} />
      <h6 className="mb-0 fw-bold" style={{ fontSize: '0.95rem' }}>{title}</h6>
    </div>
    <div style={{ fontSize: '0.85rem', paddingLeft: 26 }}>
      {children}
    </div>
  </div>
);

const GuideAcknowledgment = ({ show, onAcknowledge }) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await onAcknowledge(GUIDE_VERSION);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: '1.15rem' }}>
          <ClipboardList size={24} />
          Guide Chef de Projet &mdash; Wilout Operational
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto', fontSize: '0.88rem' }}>
        <div className="alert alert-info d-flex align-items-center gap-2 mb-3" style={{ borderRadius: 'var(--ui-border-radius-lg)' }}>
          <HelpCircle size={18} style={{ flexShrink: 0 }} />
          <span>Merci de lire attentivement ce guide. Il d&eacute;taille tes responsabilit&eacute;s et les bonnes pratiques &agrave; suivre sur la plateforme.</span>
        </div>

        {/* RÈGLE D'OR */}
        <div className="p-3 mb-3 rounded-3" style={{
          background: 'var(--ui-color-primary-light, #ecfdf5)',
          border: '2px solid var(--ui-color-primary)',
          borderRadius: 'var(--ui-border-radius-lg)'
        }}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <AlertTriangle size={20} style={{ color: 'var(--ui-color-primary)' }} />
            <h6 className="mb-0 fw-bold" style={{ fontSize: '1rem', color: 'var(--ui-color-primary)' }}>R&egrave;gle d'or</h6>
          </div>
          <p className="mb-0" style={{ fontSize: '0.9rem' }}>
            <strong>Si ce n'est pas rempli dans l'outil, c'est consid&eacute;r&eacute; comme non fait.</strong> Le planning, les fiches event, les logs d'astreinte, les mises &agrave; jour&hellip; tout doit &ecirc;tre saisi au fur et &agrave; mesure. C'est ce qui permet &agrave; l'&eacute;quipe de piloter, de prendre des d&eacute;cisions et de te remplacer sans accroc en cas d'absence.
          </p>
        </div>

        {/* SECTION PRINCIPALE : Suivi de la fiche événement */}
        <div className="p-3 mb-3 rounded-3 alert alert-warning" style={{
          borderLeft: '4px solid var(--ui-color-warning, #f59e0b)'
        }}>
          <Section icon={AlertTriangle} title="Ta responsabilit&eacute; principale : tenir ta fiche &eacute;v&eacute;nement &agrave; jour" important>
            <p className="mb-2">
              <strong>L'outil doit refl&eacute;ter la r&eacute;alit&eacute; &agrave; tout moment.</strong> Si quelqu'un doit prendre la main sur ton &eacute;v&eacute;nement, il doit pouvoir le faire facilement en consultant la fiche.
            </p>
            <ul className="mb-0">
              <li><strong>Remplis ta fiche au fur et &agrave; mesure</strong> &mdash; ne laisse pas les infos s'accumuler. Mets &agrave; jour d&egrave;s qu'il y a un changement.</li>
              <li><strong>Coche les t&acirc;ches une fois effectu&eacute;es</strong> &mdash; la checklist doit toujours refl&eacute;ter l'&eacute;tat r&eacute;el de la pr&eacute;paration.</li>
              <li><strong>Mets &agrave; jour les dates</strong> &mdash; dates de livraison, dates de d&eacute;placement, dates d'&eacute;v&eacute;nement. Tout doit &ecirc;tre &agrave; jour.</li>
              <li><strong>Ajoute des commentaires</strong> &mdash; documente les &eacute;changes, les d&eacute;cisions, les probl&egrave;mes rencontr&eacute;s. C'est la m&eacute;moire de l'&eacute;v&eacute;nement.</li>
              <li><strong>Connecte le client Sellsy</strong> &mdash; lie le client &agrave; l'&eacute;v&eacute;nement pour retrouver les documents commerciaux.</li>
              <li><strong>Uploade les fichiers utiles</strong> &mdash; plans, bons de commande, &eacute;changes mails importants.</li>
            </ul>
          </Section>
        </div>

        <Section icon={ClipboardList} title="Ton Dashboard">
          <ul className="mb-1">
            <li><strong>Mes t&acirc;ches</strong> &mdash; tes t&acirc;ches assign&eacute;es, tri&eacute;es par urgence. Change leur statut directement.</li>
            <li><strong>Prochains d&eacute;placements</strong> &mdash; tes prochains &eacute;v&eacute;nements. Vert = feuille de route valid&eacute;e, orange = en cours.</li>
          </ul>
        </Section>

        <Section icon={CheckCircle2} title="La Checklist">
          <p className="mb-1">Chaque &eacute;v&eacute;nement a une checklist de pr&eacute;paration :</p>
          <ul className="mb-1">
            <li>Rond vide = &agrave; faire</li>
            <li>Horloge orange = en cours</li>
            <li>Check vert = termin&eacute;</li>
          </ul>
          <p className="mb-0"><strong>Coche chaque t&acirc;che au moment o&ugrave; tu la r&eacute;alises, pas &agrave; la fin.</strong> Cela permet &agrave; tout le monde de voir o&ugrave; en est la pr&eacute;paration en temps r&eacute;el.</p>
        </Section>

        <Section icon={RefreshCw} title="Mise &agrave; jour continue">
          <ul className="mb-0">
            <li><strong>Dates de livraison</strong> &mdash; mets-les &agrave; jour d&egrave;s qu'elles changent</li>
            <li><strong>Mat&eacute;riel requis</strong> &mdash; v&eacute;rifie et ajuste les quantit&eacute;s</li>
            <li><strong>Informations client</strong> &mdash; garde les coordonn&eacute;es &agrave; jour</li>
            <li><strong>Statut de l'&eacute;v&eacute;nement</strong> &mdash; fais &eacute;voluer le statut au bon moment</li>
          </ul>
        </Section>

        <Section icon={MessageSquarePlus} title="Commentaires">
          <p className="mb-0">
            <strong>Commente r&eacute;guli&egrave;rement</strong> sur tes &eacute;v&eacute;nements : &eacute;changes avec le client, d&eacute;cisions prises, points d'attention, probl&egrave;mes r&eacute;solus. Un &eacute;v&eacute;nement bien document&eacute; = une passation facile.
          </p>
        </Section>

        <Section icon={Users} title="Passation & Collaboration">
          <p className="mb-0">
            L'objectif est que <strong>n'importe quel coll&egrave;gue puisse reprendre ton &eacute;v&eacute;nement</strong> en lisant la fiche. Si les infos sont compl&egrave;tes et &agrave; jour, la passation se fait sans friction.
          </p>
        </Section>

        <Section icon={MapPin} title="D&eacute;placements & Feuille de Route">
          <ul className="mb-0">
            <li>Ta <strong>feuille de route personnalis&eacute;e</strong> contient tes transports, h&eacute;bergements et repas.</li>
            <li>T&eacute;l&eacute;charge-la depuis le dashboard ou la page &eacute;v&eacute;nement.</li>
            <li>La <strong>fiche de d&eacute;ploiement</strong> contient les infos techniques.</li>
          </ul>
        </Section>

        <Section icon={CalendarDays} title="Planning mensuel">
          <p className="mb-1">
            Ton planning mensuel (bureau, t&eacute;l&eacute;travail, d&eacute;placements, repos, cong&eacute;s, r&eacute;cup, astreintes) est <strong>import&eacute; par l'admin</strong> dans l'outil. Ton job&nbsp;:
          </p>
          <ul className="mb-0">
            <li><strong>V&eacute;rifier que ton planning est correct</strong> d&egrave;s qu'il est en ligne et signaler imm&eacute;diatement toute erreur.</li>
            <li><strong>Mettre &agrave; jour ton planning d&egrave;s qu'il y a un changement</strong> en cours de mois (un d&eacute;placement qui bouge, un cong&eacute; pos&eacute;, une r&eacute;cup pr&eacute;vue, etc.).</li>
            <li><strong>Soumettre ton planning pour validation</strong> une fois v&eacute;rifi&eacute;.</li>
          </ul>
        </Section>

        <Section icon={Phone} title="Astreintes & logs d'interventions">
          <p className="mb-1">
            Pendant ta p&eacute;riode d'astreinte, <strong>chaque appel re&ccedil;u doit &ecirc;tre log&eacute;</strong> dans l'outil &mdash; m&ecirc;me un appel court, m&ecirc;me un appel pour rien.
          </p>
          <ul className="mb-0">
            <li><strong>Logger l'intervention</strong> &mdash; bouton &laquo; Logger appel &raquo; depuis &laquo; Mes astreintes &raquo;. Indique le motif, l'event concern&eacute; si applicable, la dur&eacute;e et la r&eacute;solution.</li>
            <li><strong>Si l'intervention est r&eacute;solue</strong> &mdash; marque-la r&eacute;solue. Sinon, garde-la ouverte le temps qu'il faut.</li>
            <li><strong>Pourquoi c'est important</strong> &mdash; ces logs alimentent le suivi de la charge d'astreinte, le digest hebdomadaire de l'&eacute;quipe et la r&eacute;mun&eacute;ration. <em>Pas de log = pas reconnu.</em></li>
          </ul>
        </Section>

        <Section icon={FileText} title="Retex (Retour d'Exp&eacute;rience)">
          <p className="mb-0"><strong>&Agrave; faire apr&egrave;s chaque &eacute;v&eacute;nement.</strong> Note ta satisfaction (1 &agrave; 10) et d&eacute;cris ce qui a bien fonctionn&eacute; et ce qui peut &ecirc;tre am&eacute;lior&eacute;.</p>
        </Section>

        <Section icon={Send} title="Probl&egrave;mes & Id&eacute;es">
          <ul className="mb-0">
            <li><strong>Suggestions</strong> &mdash; signale un bug ou une demande de modification</li>
            <li><strong>Demandes Dev</strong> &mdash; propose une nouvelle fonctionnalit&eacute;</li>
          </ul>
        </Section>

        <hr />

        <div className="p-3 rounded-3" style={{ backgroundColor: 'var(--ui-color-bg-muted)', border: '1px solid var(--ui-color-border)' }}>
          <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>
            <Calendar size={16} className="me-2" style={{ color: 'var(--ui-color-primary)' }} />
            En r&eacute;sum&eacute; : ce que tu dois faire
          </h6>
          <div className="table-responsive">
          <table className="table table-sm mb-0" style={{ fontSize: '0.82rem' }}>
            <tbody>
              <tr><td className="fw-semibold" style={{ width: '40%' }}>En continu</td><td>Tenir ta fiche &eacute;v&eacute;nement &agrave; jour, cocher les t&acirc;ches, commenter</td></tr>
              <tr><td className="fw-semibold">Chaque jour</td><td>Consulter ton dashboard, avancer tes t&acirc;ches</td></tr>
              <tr><td className="fw-semibold">Chaque mois</td><td>V&eacute;rifier ton planning import&eacute; par l'admin et le soumettre</td></tr>
              <tr><td className="fw-semibold">Pendant une astreinte</td><td>Logger chaque appel re&ccedil;u, m&ecirc;me bref</td></tr>
              <tr><td className="fw-semibold">D&egrave;s qu'il y a un changement</td><td>Mettre &agrave; jour les dates, le mat&eacute;riel, le statut, le planning</td></tr>
              <tr><td className="fw-semibold">Avant un &eacute;v&eacute;nement</td><td>V&eacute;rifier que la checklist est compl&egrave;te, t&eacute;l&eacute;charger la feuille de route</td></tr>
              <tr><td className="fw-semibold">Apr&egrave;s un &eacute;v&eacute;nement</td><td>R&eacute;diger le retex</td></tr>
              <tr><td className="fw-semibold">Quand n&eacute;cessaire</td><td>Connecter le client Sellsy, uploader les fichiers, signaler un bug</td></tr>
            </tbody>
          </table>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="w-100">
          <Form.Check
            type="checkbox"
            id="guide-ack-check"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            label={
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                J'ai lu et compris mes responsabilit&eacute;s en tant que chef de projet. Je m'engage &agrave; tenir mes fiches &eacute;v&eacute;nements &agrave; jour, &agrave; remplir mon planning mensuel, &agrave; logger mes interventions d'astreinte, et &agrave; respecter la r&egrave;gle d'or : <strong>si ce n'est pas rempli, c'est consid&eacute;r&eacute; comme non fait.</strong>
              </span>
            }
            className="mb-3"
          />
          <Button
            variant="primary"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={handleSubmit}
            disabled={!checked || submitting}
            style={{ borderRadius: 'var(--ui-border-radius-lg)', padding: '10px 0', fontWeight: 600 }}
          >
            {submitting ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <CheckCircle2 size={18} />
            )}
            Signer et continuer
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export { GUIDE_VERSION };
export default GuideAcknowledgment;
